import { useState, useEffect } from "react";
import { apiGet } from "../../utils/apiClient";

const PayoutTransactionList = ({ storeCode, payoutPeriodDate, isCompleted }) => {
  const [methodGroups, setMethodGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openMethods, setOpenMethods] = useState([]);

  useEffect(() => {
    loadTransactions();
    setOpenMethods([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeCode, payoutPeriodDate]);

  const loadTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ storeCode, payoutPeriodDate });
      const endpoint = isCompleted ? "/admin/payout/complete/product" : "/admin/payout/pending/product";
      const response = await apiGet(`${endpoint}?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        setMethodGroups(Array.isArray(data) ? data : (data.content || []));
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "거래 목록 조회에 실패했습니다.");
        setMethodGroups([]);
      }
    } catch (err) {
      setError(err.message || "거래 목록 조회에 실패했습니다.");
      console.error("거래 목록 조회 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleMethod = (index) => {
    setOpenMethods((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  return (
    <div className="adminpayoutstore-list">
      {loading ? (
        <div className="adminpayoutstore-loading">로딩 중</div>
      ) : error ? (
        <div className="adminpayoutstore-error">{error}</div>
      ) : methodGroups.length === 0 ? (
        <div className="adminpayoutstore-error">데이터가 없습니다.</div>
      ) : (
        <div className="adminpayoutstore-method-list">
          {methodGroups.map((group, index) => {
            const isOpen = openMethods.includes(index);
            const products = group.products || [];
            const totalPrice = products.reduce((sum, p) => sum + (p.productAmount ?? 0) * (p.productPrice ?? 0), 0);
            const payoutBaseTax = products.reduce((sum, p) => sum + (p.payoutBaseTax ?? 0), 0);
            const payoutCustomTax = products.reduce((sum, p) => sum + (p.payoutCustomTax ?? 0), 0);
            const payoutFinal = products.reduce((sum, p) => sum + (p.productFinal ?? 0), 0);
            return (
              <div
                key={group.paymentType ?? index}
                className={`adminpayoutstore-method-card ${isOpen ? "adminpayoutstore-method-card-open" : ""}`}
              >
                <div
                  className="adminpayoutstore-method-header"
                  onClick={() => toggleMethod(index)}
                >
                  <div className="adminpayoutstore-method-name">
                    {group.paymentType}
                    <span className="adminpayoutstore-method-count">{products.length.toLocaleString()}건</span>
                  </div>
                  <div className="adminpayoutstore-method-summary">
                    <div className="adminpayoutstore-method-summary-item">
                      <span className="adminpayoutstore-method-summary-label">총 매출액</span>
                      <span className="adminpayoutstore-method-summary-value">{totalPrice.toLocaleString()}원</span>
                    </div>
                    <div className="adminpayoutstore-method-summary-item">
                      <span className="adminpayoutstore-method-summary-label">PG 수수료</span>
                      <span className="adminpayoutstore-method-summary-value">{payoutBaseTax.toLocaleString()}원</span>
                    </div>
                    <div className="adminpayoutstore-method-summary-item">
                      <span className="adminpayoutstore-method-summary-label">자사 수수료</span>
                      <span className="adminpayoutstore-method-summary-value">{payoutCustomTax.toLocaleString()}원</span>
                    </div>
                    <div className="adminpayoutstore-method-summary-item">
                      <span className="adminpayoutstore-method-summary-label">실 정산액</span>
                      <span className="adminpayoutstore-method-summary-value adminpayoutstore-method-summary-final">
                        {payoutFinal.toLocaleString()}원
                      </span>
                    </div>
                  </div>
                  <span className={`adminpayoutstore-method-arrow ${isOpen ? "adminpayoutstore-method-arrow-open" : ""}`}>
                    ▼
                  </span>
                </div>

                {isOpen && (
                  <div className="adminpayoutstore-method-body">
                    <table className="adminpayoutstore-table">
                      <thead>
                        <tr>
                          <th>상품코드</th>
                          <th>상품명</th>
                          <th>수량</th>
                          <th>판매가</th>
                          <th>총 매출액</th>
                          <th>PG 수수료</th>
                          <th>자사 수수료</th>
                          <th>실 정산액</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.length === 0 ? (
                          <tr>
                            <td colSpan="8" style={{ textAlign: "center", fontWeight: "bold" }}>
                              데이터가 없습니다.
                            </td>
                          </tr>
                        ) : (
                          products.map((tx, index) => (
                            <tr key={tx.productCode ?? index}>
                              <td>{tx.productCode}</td>
                              <td>{tx.productNm}</td>
                              <td>{tx.productAmount?.toLocaleString()}</td>
                              <td>{tx.productPrice?.toLocaleString()}원</td>
                              <td>{((tx.productAmount ?? 0) * (tx.productPrice ?? 0)).toLocaleString()}원</td>
                              <td>{tx.payoutBaseTax?.toLocaleString()}원</td>
                              <td>{tx.payoutCustomTax?.toLocaleString()}원</td>
                              <td>{tx.productFinal?.toLocaleString()}원</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PayoutTransactionList;
